import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { Box, Button, Typography } from "@mui/material";
import Cookies from "js-cookie";
import React from "react";
import { useNavigate } from "react-router-dom";
import biditLogo from "../asset/images/logo.png";
import { trackEvent } from "../asset/js/analytics";

const BRAND = {
  blue: "#20AEEF",
  blueDark: "#087FC1",
  navy: "#17384E",
  orange: "#F59C1A",
  orangeDark: "#D97900",
  aqua: "#22C7D6",
};

const steps = [
  {
    number: "01",
    title: "מפרסמים עבודה",
    text: "מתארים בקצרה מה צריך לעשות וקובעים כמה זמן המכרז יהיה פתוח.",
    Icon: DescriptionRoundedIcon,
    accent: BRAND.blue,
    soft: "#EAF7FE",
    extra: (
      <Box
        sx={{
          mt: 1.15,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          px: 1.05,
          py: 0.5,
          borderRadius: "999px",
          bgcolor: "#F2FAFE",
          color: "#456F87",
        }}
      >
        <AccessTimeRoundedIcon sx={{ fontSize: 16, color: BRAND.orange }} />
        <Typography sx={{ fontFamily: "Assistant", fontWeight: 800, fontSize: 12.5 }}>
          המכרז פועל בזמן שאתם קובעים
        </Typography>
      </Box>
    ),
  },
  {
    number: "02",
    title: "מקבלים הצעות מחיר",
    text: "בזמן שהמכרז פתוח, בעלי מקצוע מתאימים מגישים הצעות ומתחרים על העבודה.",
    Icon: GroupsRoundedIcon,
    accent: BRAND.aqua,
    soft: "#EAFBFD",
    extra: (
      <Box sx={{ mt: 1.15, display: "flex", gap: 0.65, flexWrap: "wrap" }}>
        {["₪850", "₪720", "₪640"].map((price, index) => (
          <Box
            key={price}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: "999px",
              border: "1px solid",
              borderColor: index === 2 ? "#BDEFF3" : "#E1EAF0",
              bgcolor: index === 2 ? "#ECFBFC" : "#F7F9FB",
              color: index === 2 ? "#158D99" : "#6D8190",
              fontFamily: "Assistant",
              fontWeight: index === 2 ? 900 : 750,
              fontSize: 12.5,
              animation: `priceFloat 4.6s ease-in-out ${index * 0.22}s infinite`,
              "@keyframes priceFloat": {
                "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
                "50%": { transform: `translateY(-4px) rotate(${index === 1 ? 1.3 : -1.3}deg)` },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            {price}
          </Box>
        ))}
      </Box>
    ),
  },
  {
    number: "03",
    title: "Bidit מדרגת אוטומטית",
    text: "כשהמכרז מסתיים, ההצעות מדורגות לפי המחיר וההצעה הזולה ביותר עולה למקום הראשון.",
    Icon: EmojiEventsRoundedIcon,
    accent: BRAND.orange,
    soft: "#FFF5E7",
    extra: (
      <Box
        sx={{
          mt: 1.15,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.65,
          px: 1.05,
          py: 0.52,
          borderRadius: "999px",
          bgcolor: "#FFF8ED",
          color: BRAND.orangeDark,
          fontFamily: "Assistant",
          fontWeight: 900,
          fontSize: 12.5,
          animation: "winnerPulse 4.8s ease-in-out infinite",
          "@keyframes winnerPulse": {
            "0%, 100%": { transform: "translateY(0) scale(1)", boxShadow: "0 0 0 rgba(246,167,47,0)" },
            "50%": { transform: "translateY(-3px) scale(1.015)", boxShadow: "0 8px 20px rgba(246,167,47,.16)" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <EmojiEventsRoundedIcon sx={{ fontSize: 17 }} />
        ₪640 במקום הראשון
      </Box>
    ),
  },
  {
    number: "04",
    title: "אתם מחליטים אם להתקדם",
    text: "Bidit מציגה את ההצעה המובילה ואת כרטיס בעל המקצוע. אתם מחליטים אם לאשר או לעבור להצעה הבאה.",
    Icon: TaskAltRoundedIcon,
    accent: BRAND.navy,
    soft: "#EDF4F7",
    showLogo: true,
  },
];

function FlowConnector({ direction = "left" }) {
  const path = direction === "left"
    ? "M 495 8 C 470 45, 390 54, 305 49 C 218 44, 138 51, 105 82"
    : "M 105 8 C 135 45, 215 54, 300 49 C 388 44, 468 51, 495 82";

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "relative",
        height: { xs: 58, sm: 76 },
        mx: { xs: 1, sm: 4 },
        overflow: "visible",
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 600 90"
        preserveAspectRatio="none"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
      >
        <defs>
          <linearGradient id={`flow-${direction}`} x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor={BRAND.blue} />
            <stop offset="58%" stopColor={BRAND.aqua} />
            <stop offset="100%" stopColor={BRAND.orange} />
          </linearGradient>
          <marker
            id={`arrow-${direction}`}
            markerWidth="9"
            markerHeight="9"
            refX="7"
            refY="4.5"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path d="M0,0 L0,9 L8,4.5 z" fill={BRAND.orange} />
          </marker>
        </defs>
        <path d={path} fill="none" stroke="#E5EEF4" strokeWidth="5" strokeLinecap="round" />
        <path
          d={path}
          fill="none"
          stroke={`url(#flow-${direction})`}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="12 9"
          markerEnd={`url(#arrow-${direction})`}
          sx={{
            animation: "riverMove 2.4s linear infinite",
            "@keyframes riverMove": { to: { strokeDashoffset: -42 } },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      </Box>

      {[23, 50, 77].map((left, index) => (
        <Box
          key={left}
          sx={{
            position: "absolute",
            left: `${left}%`,
            top: index === 1 ? "48%" : "42%",
            width: index === 1 ? 7 : 5,
            height: index === 1 ? 7 : 5,
            borderRadius: "50%",
            bgcolor: index === 2 ? BRAND.orange : BRAND.blue,
            opacity: 0.45,
            animation: `sparkleDrift ${2.8 + index * 0.35}s ease-in-out ${index * 0.3}s infinite`,
            "@keyframes sparkleDrift": {
              "0%,100%": { transform: "translateY(0) scale(.85)", opacity: 0.28 },
              "50%": { transform: "translateY(-5px) scale(1.15)", opacity: 0.68 },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      ))}
    </Box>
  );
}

function StepCard({ step, index }) {
  const Icon = step.Icon;
  const alignLeft = index % 2 === 0;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: { xs: alignLeft ? "flex-start" : "flex-end", sm: alignLeft ? "flex-start" : "flex-end" },
        position: "relative",
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          width: { xs: "91%", sm: "76%", md: "68%" },
          position: "relative",
          borderRadius: { xs: "25px", md: "30px" },
          bgcolor: "rgba(255,255,255,.97)",
          border: "1px solid #D5E5EE",
          borderTop: `4px solid ${step.accent}`,
          boxShadow: "0 14px 34px rgba(23,56,78,.10)",
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.55, sm: 1.8 },
          transition: "transform .25s ease, box-shadow .25s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 20px 42px rgba(23,56,78,.15)",
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "54px minmax(0, 1fr)",
            gap: 1.35,
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "19px",
              display: "grid",
              placeItems: "center",
              bgcolor: step.soft,
              color: step.accent,
              flexShrink: 0,
              position: "relative",
              overflow: "hidden",
              animation: `iconBob 5s ease-in-out ${index * 0.45}s infinite`,
              "@keyframes iconBob": {
                "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
                "50%": { transform: `translateY(-4px) rotate(${index % 2 === 0 ? -2 : 2}deg)` },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Icon sx={{ fontSize: 26, position: "relative", zIndex: 1 }} />
          </Box>

          <Box sx={{ minWidth: 0, pt: 0.1 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
              <Typography
                component="span"
                sx={{
                  fontFamily: "Assistant",
                  fontWeight: 900,
                  color: step.accent,
                  fontSize: 11.5,
                  letterSpacing: ".06em",
                }}
              >
                שלב {step.number}
              </Typography>
              <Typography
                component="h3"
                sx={{
                  fontFamily: "Assistant",
                  fontWeight: 900,
                  color: BRAND.navy,
                  fontSize: { xs: 17, sm: 19 },
                  lineHeight: 1.2,
                }}
              >
                {step.title}
              </Typography>
            </Box>
            <Typography
              sx={{
                mt: 0.55,
                fontFamily: "Assistant",
                color: "#637B8C",
                fontSize: { xs: 13.2, sm: 14.2 },
                lineHeight: 1.55,
              }}
            >
              {step.text}
            </Typography>
            {step.extra}

            {step.showLogo && (
              <Box
                sx={{
                  mt: 1.3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.1,
                  px: 1.15,
                  py: 0.9,
                  borderRadius: "18px",
                  bgcolor: "#FFF9EF",
                  border: "1px solid #F7D79C",
                  boxShadow: "0 7px 18px rgba(245,156,26,.10)",
                  maxWidth: "100%",
                }}
              >
                <Box
                  component="img"
                  src={biditLogo}
                  alt="Bidit"
                  sx={{
                    width: { xs: 52, sm: 58 },
                    height: { xs: 52, sm: 58 },
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                    filter: "drop-shadow(0 5px 9px rgba(23,56,78,.12))",
                    animation: "logoNod 4.6s ease-in-out infinite",
                    "@keyframes logoNod": {
                      "0%,100%": { transform: "rotate(-1deg) translateY(0) scale(1)" },
                      "50%": { transform: "rotate(2deg) translateY(-3px) scale(1.04)" },
                    },
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: BRAND.navy, fontSize: { xs: 13.2, sm: 14 } }}>
                    Bidit מסדרת את הדרך
                  </Typography>
                  <Typography sx={{ mt: 0.15, fontFamily: "Assistant", fontWeight: 700, color: "#667B88", fontSize: { xs: 12.2, sm: 12.8 }, lineHeight: 1.35 }}>
                    ההחלטה אם להתקדם נשארת אצלכם.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function BidFlowJourney() {
  const navigate = useNavigate();

  const startPublishing = () => {
    trackEvent("publish_clicked", {
      authenticated: Boolean(Cookies.get("token")),
      path: "home_flow_journey",
    });
    navigate(Cookies.get("token") ? "/postbid" : "/signupwithphone");
  };

  const focusAssistant = () => {
    document.getElementById("bidit-tender-assistant")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <Box
      component="section"
      dir="rtl"
      aria-labelledby="bidit-flow-title"
      sx={{
        mb: { xs: 2, md: 2.6 },
        px: { xs: 1.2, sm: 2.2, md: 3 },
        py: { xs: 2.3, sm: 3, md: 3.6 },
        borderRadius: { xs: "24px", md: "32px" },
        background: "linear-gradient(180deg, #F7FCFF 0%, #EDF9FE 46%, #FFF4E3 100%)",
        border: "1px solid #CFE4EF",
        boxShadow: "0 14px 40px rgba(23,56,78,.09)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "absolute", width: 270, height: 270, borderRadius: "50%", bgcolor: "rgba(32,174,239,.11)", top: -155, right: -95 }} />
      <Box sx={{ position: "absolute", width: 230, height: 230, borderRadius: "50%", bgcolor: "rgba(245,156,26,.12)", bottom: -135, left: -80 }} />

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 940, mx: "auto" }}>
        <Box sx={{ textAlign: "center", maxWidth: 720, mx: "auto", mb: { xs: 2.1, sm: 2.8 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.65,
              px: 1.15,
              py: 0.5,
              borderRadius: "999px",
              bgcolor: "#EEF8FD",
              color: "#466D86",
              mb: 0.85,
            }}
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: BRAND.orange, animation: "sparkle 3.8s ease-in-out infinite", "@keyframes sparkle": { "0%,100%": { transform: "rotate(0deg) scale(1)" }, "50%": { transform: "rotate(9deg) scale(1.12)" } }, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }} />
            <Typography sx={{ fontFamily: "Assistant", fontWeight: 850, fontSize: 12.5 }}>
              מכרז דיגיטלי, בדרך פשוטה
            </Typography>
          </Box>

          <Typography
            id="bidit-flow-title"
            component="h2"
            sx={{
              fontFamily: "Assistant",
              fontWeight: 900,
              color: BRAND.navy,
              fontSize: { xs: 24, sm: 29, md: 33 },
              lineHeight: 1.15,
              letterSpacing: "-.02em",
            }}
          >
            כך העבודה שלכם מתקדמת ב-<Box component="span" sx={{ color: BRAND.orangeDark, font: "inherit" }}>Bidit</Box>
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: "Assistant",
              color: "#587283",
              fontSize: { xs: 14, sm: 15.3 },
              lineHeight: 1.55,
            }}
          >
            אתם קובעים את זמן המכרז. בעלי המקצוע מגישים הצעות, Bidit מדרגת, ואתם מחליטים אם להתקדם.
          </Typography>
        </Box>

        <Box sx={{ position: "relative" }}>
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <StepCard step={step} index={index} />
              {index < steps.length - 1 && (
                <FlowConnector direction={index % 2 === 0 ? "left" : "right"} />
              )}
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ mt: { xs: 2.3, sm: 3 }, textAlign: "center" }}>
          <Typography sx={{ fontFamily: "Assistant", fontWeight: 900, color: BRAND.navy, fontSize: { xs: 17, sm: 19 } }}>
            מוכנים להתחיל?
          </Typography>
          <Typography sx={{ mt: 0.3, fontFamily: "Assistant", color: "#718694", fontSize: 13.5 }}>
            פרסום המכרז פשוט ומהיר.
          </Typography>

          <Box sx={{ mt: 1.35, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
            <Button
              onClick={startPublishing}
              variant="contained"
              sx={{
                minHeight: 46,
                px: 3,
                borderRadius: "14px",
                fontFamily: "Assistant",
                fontWeight: 900,
                textTransform: "none",
                background: `linear-gradient(135deg, ${BRAND.blueDark}, ${BRAND.blue})`,
                border: `2px solid ${BRAND.orange}`,
                boxShadow: "0 10px 24px rgba(8,127,193,.27)",
                "&:hover": { background: `linear-gradient(135deg, #066DA6, ${BRAND.blueDark})`, transform: "translateY(-1px)", boxShadow: "0 13px 28px rgba(8,127,193,.32)" },
                transition: "transform .2s ease, box-shadow .2s ease, background .2s ease",
              }}
            >
              פרסום עבודה בחינם
            </Button>
            <Button
              onClick={focusAssistant}
              variant="text"
              sx={{
                minHeight: 46,
                px: 1.5,
                borderRadius: "14px",
                fontFamily: "Assistant",
                fontWeight: 850,
                textTransform: "none",
                color: "#526F84",
                "&:hover": { bgcolor: "#F2F8FB" },
              }}
            >
              לא בטוחים מה לכתוב? סייען המכרז יעזור
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default BidFlowJourney;
